import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AppSettings } from '@/types'

export const useSettingsStore = defineStore('settings', () => {
  const settings = ref<AppSettings>({
    theme: 'auto',
    language: 'en',
    units: 'metric',
    distanceUnit: 'km',
    speedUnit: 'kmh',
    fuelConsumptionUnit: 'l100km',
    currency: 'EUR',
    dateFormat: 'YYYY-MM-DD',
    timeFormat: '24h'
  })

  const mapLanguagePreference = ref<'local' | 'en'>('local')

  const isDarkMode = ref(false)

  // Computed refs for easy access
  const theme = computed(() => settings.value.theme)
  const language = computed(() => settings.value.language)
  const units = computed(() => settings.value.units)
  const currency = computed(() => settings.value.currency)
  const mapLanguage = computed(() => mapLanguagePreference.value)

  function updateSettings(newSettings: Partial<AppSettings>) {
    settings.value = { ...settings.value, ...newSettings }
    saveSettings()
  }

  function setLanguage(lang: 'en' | 'hu' | 'de' | 'fr' | 'it' | 'es' | 'pl' | 'ro') {
    settings.value.language = lang
    saveSettings()
    // Here you can add i18n language change logic later
    console.log('Language changed to:', lang)
  }

  function setTheme(theme: 'light' | 'dark' | 'auto') {
    settings.value.theme = theme
    applyTheme()
    saveSettings()
  }

  function setMapLanguage(lang: 'local' | 'en') {
    mapLanguagePreference.value = lang
    saveMapLanguage()
    console.log('Map language changed to:', lang)
  }

  function setDistanceUnit(unit: 'km' | 'mi') {
    settings.value.distanceUnit = unit
    saveSettings()
  }

  function setSpeedUnit(unit: 'kmh' | 'mph') {
    settings.value.speedUnit = unit
    saveSettings()
  }

  function setFuelConsumptionUnit(unit: 'l100km' | 'mpg' | 'kml') {
    settings.value.fuelConsumptionUnit = unit
    saveSettings()
  }

  function setCurrency(currency: string) {
    settings.value.currency = currency
    saveSettings()
  }

  function setDateFormat(format: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD') {
    settings.value.dateFormat = format
    saveSettings()
  }

  function setTimeFormat(format: '12h' | '24h') {
    settings.value.timeFormat = format
    saveSettings()
  }

  function toggleDarkMode() {
    isDarkMode.value = !isDarkMode.value
    applyTheme()
  }

  function applyTheme() {
    const { theme } = settings.value
    
    // Remove existing theme classes
    document.documentElement.classList.remove('ion-palette-dark')
    document.body.classList.remove('dark')
    
    if (theme === 'dark') {
      isDarkMode.value = true
      document.documentElement.classList.add('ion-palette-dark')
      document.body.classList.add('dark')
    } else if (theme === 'light') {
      isDarkMode.value = false
      // Light mode is default, no class needed
    } else {
      // Auto theme - follow system preference
      isDarkMode.value = window.matchMedia('(prefers-color-scheme: dark)').matches
      if (isDarkMode.value) {
        document.documentElement.classList.add('ion-palette-dark')
        document.body.classList.add('dark')
      }
    }
    
    console.log('Theme applied:', theme, 'isDark:', isDarkMode.value)
  }

  function saveSettings() {
    localStorage.setItem('cartrip-settings', JSON.stringify(settings.value))
  }

  function saveMapLanguage() {
    localStorage.setItem('cartrip-map-language', mapLanguagePreference.value)
  }

  function loadSettings() {
    const saved = localStorage.getItem('cartrip-settings')
    if (saved) {
      try {
        settings.value = { ...settings.value, ...JSON.parse(saved) }
      } catch (error) {
        console.warn('Failed to load settings:', error)
      }
    }
    
    // Load map language preference
    const savedMapLang = localStorage.getItem('cartrip-map-language')
    if (savedMapLang === 'local' || savedMapLang === 'en') {
      mapLanguagePreference.value = savedMapLang
    }
    
    applyTheme()
  }

  // Listen for system theme changes
  function initializeThemeListener() {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', () => {
      if (settings.value.theme === 'auto') {
        applyTheme()
      }
    })
  }

  return {
    settings,
    isDarkMode,
    theme,
    language,
    units,
    currency,
    mapLanguage,
    updateSettings,
    setLanguage,
    setTheme,
    setMapLanguage,
    setDistanceUnit,
    setSpeedUnit,
    setFuelConsumptionUnit,
    setCurrency,
    setDateFormat,
    setTimeFormat,
    toggleDarkMode,
    applyTheme,
    loadSettings,
    initializeThemeListener
  }
})